import {
  connectBaseApi,
  createRpcQuery,
  createRpcMutation,
  pickupLocationClient,
} from "@racoon/api";

const pickupLocationGroupApi = connectBaseApi.injectEndpoints({
  endpoints: (build) => ({
    listPickupLocationGroups: createRpcQuery(
      build,
      pickupLocationClient,
      "listPickupLocationGroups",
      {
        providesTags: ["PickupLocationGroup"],
      },
    ),

    getPickupLocationGroupById: createRpcQuery(
      build,
      pickupLocationClient,
      "getPickupLocationGroupById",
      {
        providesTags: (_result, _error, arg) =>
          arg?.id != null ? [{ type: "PickupLocationGroup" as const, id: String(arg.id) }] : [],
      },
    ),

    // The stops a route out of the group would have on a day. Read on demand
    // when the route is opened, never cached: jobs come and go during the day.
    previewPickupLocationGroupJobs: createRpcQuery(
      build,
      pickupLocationClient,
      "previewPickupLocationGroupJobs",
      { keepUnusedDataFor: 0 },
    ),

    createPickupLocationGroup: createRpcMutation(
      build,
      pickupLocationClient,
      "createPickupLocationGroup",
      {
        invalidatesTags: ["PickupLocationGroup"],
      },
    ),

    updatePickupLocationGroup: createRpcMutation(
      build,
      pickupLocationClient,
      "updatePickupLocationGroup",
      {
        invalidatesTags: (_result, _error, arg) => [
          "PickupLocationGroup",
          ...(arg?.id != null
            ? [{ type: "PickupLocationGroup" as const, id: String(arg.id) }]
            : []),
        ],
      },
    ),

    deletePickupLocationGroup: createRpcMutation(
      build,
      pickupLocationClient,
      "deletePickupLocationGroup",
      {
        invalidatesTags: ["PickupLocationGroup"],
      },
    ),

    bindContainerGroupsToPickupLocationGroup: createRpcMutation(
      build,
      pickupLocationClient,
      "bindContainerGroupsToPickupLocationGroup",
      {
        // Skip the per-group tag without an id: String(undefined) would
        // invalidate the "undefined" tag and hit unrelated queries.
        invalidatesTags: (_result, _error, arg) => [
          "PickupLocationGroup",
          ...(arg?.pickupLocationGroupId != null
            ? [{ type: "PickupLocationGroup" as const, id: String(arg.pickupLocationGroupId) }]
            : []),
        ],
      },
    ),

    unbindContainerGroupFromPickupLocationGroup: createRpcMutation(
      build,
      pickupLocationClient,
      "unbindContainerGroupFromPickupLocationGroup",
      {
        invalidatesTags: (_result, _error, arg) => [
          "PickupLocationGroup",
          ...(arg?.pickupLocationGroupId != null
            ? [{ type: "PickupLocationGroup" as const, id: String(arg.pickupLocationGroupId) }]
            : []),
        ],
      },
    ),

    reorderContainerGroupsInPickupLocationGroup: createRpcMutation(
      build,
      pickupLocationClient,
      "reorderContainerGroupsInPickupLocationGroup",
      {
        invalidatesTags: (_result, _error, arg) =>
          arg?.pickupLocationGroupId != null
            ? [{ type: "PickupLocationGroup" as const, id: String(arg.pickupLocationGroupId) }]
            : [],
      },
    ),
  }),
  overrideExisting: false,
});

export const {
  useListPickupLocationGroupsQuery,
  useLazyListPickupLocationGroupsQuery,
  useGetPickupLocationGroupByIdQuery,
  useLazyGetPickupLocationGroupByIdQuery,
  useLazyPreviewPickupLocationGroupJobsQuery,
  useCreatePickupLocationGroupMutation,
  useUpdatePickupLocationGroupMutation,
  useDeletePickupLocationGroupMutation,
  useBindContainerGroupsToPickupLocationGroupMutation,
  useUnbindContainerGroupFromPickupLocationGroupMutation,
  useReorderContainerGroupsInPickupLocationGroupMutation,
} = pickupLocationGroupApi;
